/**
 * Component: Anonymous Session Bootstrap
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R26
 *
 * When the host page provides no JWT, the widget bootstraps an
 * anonymous session by POSTing to the deployment's public
 * ``/api/widget/anon-session`` endpoint. That endpoint returns 404
 * unless the deployment opts in (``WIDGET_ANONYMOUS_ACCESS_ENABLED``)
 * and the page origin is in the configured allow-list — so a 404
 * here means "this Galen deployment does not offer anonymous widget
 * access for this origin", which is surfaced to the host as a normal
 * init error.
 *
 * The returned token is a short-lived JWT for a low-privilege
 * ``anonymous`` principal; it is used as the Bearer on every
 * subsequent widget call exactly like a host-supplied token, and is
 * transparently re-minted on 401 / near-expiry via the existing
 * ``onAuthRefresh`` plumbing (the init pipeline wires this helper in
 * as the refresh callback when running anonymously).
 */

import { WidgetError, WidgetNetworkError } from '../api/errors';

interface AnonSessionResponse {
  token?: unknown;
  token_type?: unknown;
  expires_in?: unknown;
}

/** The widget-stable anonymous-session mint path (outside /api/v1). */
const ANON_SESSION_PATH = '/api/widget/anon-session';

/**
 * Mint an anonymous session token from ``baseUrl``.
 *
 * Resolves to the JWT string on success. Rejects with:
 *   - ``WidgetError`` on 404 (deployment not opted in / origin not
 *     allow-listed) or a malformed response body;
 *   - ``WidgetNetworkError`` on a transport failure or a non-OK,
 *     non-404 HTTP status.
 */
export async function fetchAnonSession(baseUrl: string): Promise<string> {
  const trimmed = baseUrl.replace(/\/+$/, '');
  const url = `${trimmed}${ANON_SESSION_PATH}`;

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
  } catch (e) {
    throw new WidgetNetworkError(
      `Anonymous session request to ${url} failed: ${
        e instanceof Error ? e.message : String(e)
      }`,
      e,
    );
  }

  if (resp.status === 404) {
    throw new WidgetError(
      'This Galen deployment does not offer anonymous widget access: no ' +
        'host JWT was provided and the deployment has not enabled anonymous ' +
        'mode for this page origin.',
    );
  }
  if (!resp.ok) {
    throw new WidgetNetworkError(
      `Anonymous session request to ${url} returned HTTP ${resp.status}.`,
      null,
    );
  }

  let body: AnonSessionResponse;
  try {
    body = (await resp.json()) as AnonSessionResponse;
  } catch {
    throw new WidgetError(
      `Anonymous session endpoint ${url} did not return a JSON object.`,
    );
  }
  if (typeof body.token !== 'string' || body.token.length === 0) {
    throw new WidgetError(
      `Anonymous session response from ${url} did not contain a usable token.`,
    );
  }
  return body.token;
}
